import axios from "axios";
import { environment } from "environment/environment";

axios.defaults.timeout = 15000;

export class Util {
  public static createClientTraceId(prefix = "web-order"): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${prefix}-${crypto.randomUUID()}`;
    }
    const randomPart = Math.random().toString(36).slice(2, 10);
    return `${prefix}-${Date.now()}-${randomPart}`;
  }

  public static getOrderSubmitErrorMessage(res: any): string {
    const responseData = res?.error?.response?.data;
    const error = responseData?.error || res?.error;
    const traceId =
      responseData?.traceId ||
      res?.traceId ||
      res?.error?.response?.headers?.["x-request-trace-id"];
    let message: string;

    if (error?.code === "ECONNABORTED" || error?.message?.toLowerCase?.().includes("timeout")) {
      message = "Order submission timed out. Please retry; the same submission key will be used safely.";
    } else if (typeof error === "string") {
      message = error;
    } else if (error?.message) {
      message = error.message;
    } else {
      message = "Error occurred while placing the order.";
    }

    return traceId ? `${message} Trace ID: ${traceId}` : message;
  }

  public static logOrderSubmitTiming(data: {
    endpoint: string;
    traceId: string;
    elapsedMs: number;
    success: boolean;
    role: "customer" | "admin";
    error?: any;
  }): void {
    const logData = {
      endpoint: data.endpoint,
      traceId: data.traceId,
      elapsedMs: data.elapsedMs,
      success: data.success,
      role: data.role,
      errorCode: data.error?.code,
      errorMessage: data.error?.message || (typeof data.error === "string" ? data.error : undefined),
    };

    if (!data.success || data.elapsedMs > 5000) {
      console.warn("Order submit monitor", logData);
      return;
    }

    console.info("Order submit monitor", logData);
  }

  public static apiPublicUrl(path: string): string {
    return environment.api_url + "/api/public/" + path;
  }

  public static apiAuthUrl(path: string): string {
    return environment.api_url + "/api/auth/" + path;
  }

  public static downloadOrderDetail(pdfName?: any) {
    if (pdfName) {
      const url = environment.api_url + "/orders/" + pdfName;
      return url;
    } else {
      return "";
    }
  }
  private static isValidJwtToken(token: string | null): token is string {
    if (!token) return false;

    const trimmedToken = token.trim();
    if (["null", "undefined", "", "[object Object]"].includes(trimmedToken)) {
      return false;
    }

    return trimmedToken.split(".").length === 3;
  }

  private static clearInvalidToken(): void {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
  }

  public static initAxios(): void {
    axios.interceptors.request.use(req => {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (Util.isValidJwtToken(token)) {
        req.headers.authorization = "Bearer " + token.trim();
      } else {
        delete req.headers.authorization;
        if (token) Util.clearInvalidToken();
      }
      return req;
    });

    axios.interceptors.response.use(
      function (response) {
        const traceId = response?.headers?.["x-request-trace-id"];
        if (
          traceId &&
          response.data &&
          typeof response.data === "object" &&
          !Array.isArray(response.data) &&
          !response.data.traceId
        ) {
          response.data.traceId = traceId;
        }
        return response.data;
      },
      function (error) {
        if (error?.response?.status === 401) {
          Util.clearInvalidToken();
        }
        const responseData = error?.response?.data;
        const apiError = responseData?.error;
        const networkMessage =
          !error?.response &&
          (error?.code === "ECONNABORTED" ||
            error?.code === "ERR_NETWORK" ||
            /^network error$/i.test(String(error?.message || "")) ||
            /^timeout/i.test(String(error?.message || "")))
            ? "Could not reach Zulu right now. Check your connection and try again."
            : null;
        return {
          success: false,
          data: undefined,
          error:
            (typeof apiError === "string" && apiError) ||
            networkMessage ||
            (typeof error?.message === "string" && error.message) ||
            "Could not complete that request. Try again.",
          errorCode: responseData?.errorCode,
          traceId:
            responseData?.traceId ||
            error?.response?.headers?.["x-request-trace-id"],
        };
      }
    );
  }
}

Util.initAxios();
